import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { EarlyAccessNotice, ProductShell, UpgradeNotice } from "@/components/app/product-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import { useEntitlements } from "@/lib/billing/use-entitlements";
import {
  deleteBuyerProfile,
  listBuyerProfiles,
  upsertBuyerProfile,
  verificationGaps,
  type BuyerProfile,
} from "@/lib/buyers/buyers.functions";
import {
  matchBuyersForDeal,
  matchBuyersForDisposition,
  type DealMatchResult,
} from "@/lib/buyers/match.functions";
import { listDealRecords } from "@/lib/pipeline/pipeline.functions";

export const Route = createFileRoute("/_authenticated/app/buyers")({
  head: () => ({
    meta: [
      { title: "Buyer database — AROS FLOW" },
      {
        name: "description",
        content:
          "Build buy boxes with markets, price and ARV ranges, rehab limits and proof of funds. A buyer counts as verified only once the fit facts exist.",
      },
      { property: "og:title", content: "Buyer database — AROS FLOW" },
      {
        property: "og:description",
        content: "Buy boxes with verification that is earned by the data, not assumed.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: BuyersPage,
});

function BuyersPage() {
  return (
    <ProductShell
      title="Buyers"
      subtitle="Your buy boxes, plus fit scoring against your saved deals. Scoring compares saved facts only — it never invents a buyer's appetite."
    >
      <ErrorBoundary label="Buyers crashed">
        <BuyersBody />
      </ErrorBoundary>
    </ProductShell>
  );
}

const split = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

function BuyersBody() {
  const ent = useEntitlements();
  const qc = useQueryClient();
  const fetchBuyers = useServerFn(listBuyerProfiles);
  const saveBuyer = useServerFn(upsertBuyerProfile);
  const removeBuyer = useServerFn(deleteBuyerProfile);

  const enabled = ent.loading || ent.inPlan("buyer_database");

  const { data: buyers = [], isLoading } = useQuery({
    queryKey: ["buyer-profiles"],
    queryFn: () => fetchBuyers(),
    enabled,
  });

  const [form, setForm] = useState({
    name: "",
    markets: "",
    propertyTypes: "",
    strategies: "",
    maxPrice: "",
    maxRehab: "",
    proofOfFunds: false,
    recentPurchaseDate: "",
  });

  const create = useMutation({
    mutationFn: () =>
      saveBuyer({
        data: {
          name: form.name,
          markets: split(form.markets),
          propertyTypes: split(form.propertyTypes),
          strategies: split(form.strategies),
          maxPrice: Number(form.maxPrice) || null,
          maxRehab: Number(form.maxRehab) || null,
          proofOfFunds: form.proofOfFunds,
          recentPurchaseDate: form.recentPurchaseDate || null,
        },
      }),
    onSuccess: () => {
      toast.success("Buyer saved");
      setForm({
        name: "",
        markets: "",
        propertyTypes: "",
        strategies: "",
        maxPrice: "",
        maxRehab: "",
        proofOfFunds: false,
        recentPurchaseDate: "",
      });
      qc.invalidateQueries({ queryKey: ["buyer-profiles"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => removeBuyer({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buyer-profiles"] }),
  });

  if (!ent.loading && !enabled) {
    return (
      <div className="space-y-5">
        <UpgradeNotice feature="Buyer database and matching" plan="Closer" />
        <EarlyAccessNotice
          title="Disposition sends"
          body="Blast messaging to your buyer list is still being built. Fit scoring against your saved deals works today; sending does not."
          requires="A messaging provider and consent handling for outbound sends."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <BuyerMatchPanel buyerCount={buyers.length} />

      <DispositionPanel buyerCount={buyers.length} />

      <EarlyAccessNotice
        title="Disposition sends"
        body="You can score buyers against a saved deal today. One-click blasts to your buyer list are still being built, so nothing here messages anyone."
        requires="A messaging provider and consent handling for outbound sends."
      />

      <form
        className="grid gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.name.trim()) return;
          create.mutate();
        }}
      >
        <Field id="b-name" label="Buyer / company">
          <input
            id="b-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-markets" label="Markets / counties (comma separated)">
          <input
            id="b-markets"
            value={form.markets}
            onChange={(e) => setForm({ ...form, markets: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-types" label="Property types">
          <input
            id="b-types"
            value={form.propertyTypes}
            onChange={(e) => setForm({ ...form, propertyTypes: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-strategies" label="Strategies">
          <input
            id="b-strategies"
            value={form.strategies}
            onChange={(e) => setForm({ ...form, strategies: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-max" label="Max purchase price">
          <input
            id="b-max"
            inputMode="numeric"
            value={form.maxPrice}
            onChange={(e) => setForm({ ...form, maxPrice: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-rehab" label="Max rehab">
          <input
            id="b-rehab"
            inputMode="numeric"
            value={form.maxRehab}
            onChange={(e) => setForm({ ...form, maxRehab: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="b-purchase" label="Last verified purchase">
          <input
            id="b-purchase"
            type="date"
            value={form.recentPurchaseDate}
            onChange={(e) => setForm({ ...form, recentPurchaseDate: e.target.value })}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-2 text-xs text-white/80">
            <input
              type="checkbox"
              checked={form.proofOfFunds}
              onChange={(e) => setForm({ ...form, proofOfFunds: e.target.checked })}
              className="focus-key h-4 w-4"
            />
            Proof of funds seen
          </label>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <button
            type="submit"
            disabled={create.isPending}
            className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
          >
            Save buyer
          </button>
        </div>
      </form>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading buyers…</p>
      ) : buyers.length === 0 ? (
        <p className="rounded-xl border border-[var(--glass-border)] bg-card p-6 text-sm text-muted-foreground">
          No buyers saved yet.
        </p>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {buyers.map((b: BuyerProfile) => {
            const gaps = verificationGaps(b);
            return (
              <li key={b.id} className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{b.name}</span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                      gaps.length
                        ? "border-warning/50 text-warning"
                        : "border-success/50 text-success"
                    }`}
                  >
                    {gaps.length ? "Unverified" : "Verified"}
                  </span>
                  <button
                    type="button"
                    onClick={() => del.mutate(b.id)}
                    className="focus-key ml-auto rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5"
                  >
                    Remove
                  </button>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <div>Markets: {b.markets.join(", ") || "—"}</div>
                  <div>Types: {b.propertyTypes.join(", ") || "—"}</div>
                  <div>Max price: {b.maxPrice ? `$${b.maxPrice.toLocaleString()}` : "—"}</div>
                  <div>Max rehab: {b.maxRehab ? `$${b.maxRehab.toLocaleString()}` : "—"}</div>
                </dl>
                {gaps.length > 0 && (
                  <p className="mt-2 text-[11px] text-warning">Still needed: {gaps.join(", ")}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Fit scoring against a saved deal. Deterministic — saved facts only. */
function BuyerMatchPanel({ buyerCount }: { buyerCount: number }) {
  const fetchDeals = useServerFn(listDealRecords);
  const runMatch = useServerFn(matchBuyersForDeal);
  const [dealId, setDealId] = useState("");

  const { data: deals = [], isLoading: dealsLoading } = useQuery({
    queryKey: ["deal-records"],
    queryFn: () => fetchDeals(),
  });

  const match = useMutation({
    mutationFn: (id: string) => runMatch({ data: { dealId: id } }),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
      <h2 className="text-sm font-semibold">Match a deal to your buyers</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Scores each buy box against the facts saved on the deal. Anything unknown stays unknown and
        is left out of the score rather than guessed.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <label htmlFor="match-deal" className="text-[11px] text-muted-foreground">
            Saved deal
          </label>
          <select
            id="match-deal"
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          >
            <option value="">
              {dealsLoading ? "Loading deals…" : deals.length ? "Choose a deal" : "No saved deals"}
            </option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          disabled={!dealId || match.isPending}
          onClick={() => match.mutate(dealId)}
          className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
        >
          {match.isPending ? "Scoring…" : "Score buyers"}
        </button>
      </div>

      {buyerCount === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Add a buy box below first — matching needs saved buyers to compare against.
        </p>
      )}

      {match.data && <MatchResults data={match.data} />}
    </section>
  );
}

function MatchResults({ data }: { data: DealMatchResult }) {
  return (
    <div className="mt-4 space-y-3">
      {data.matches.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active buyers to score yet.</p>
      ) : (
        data.matches.map((m) => (
          <article
            key={m.buyerId}
            className="rounded-lg border border-[var(--glass-border)] bg-background/40 p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{m.buyerName}</span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                  m.label === "Strong fit"
                    ? "border-success/50 text-success"
                    : m.label === "Possible fit"
                      ? "border-[var(--gold)]/50 text-gold"
                      : "border-warning/50 text-warning"
                }`}
              >
                {m.label}
              </span>
              {m.score != null && (
                <span className="text-xs text-muted-foreground">{m.score}/100 fit</span>
              )}
            </div>

            {m.blockers.length > 0 && (
              <ul className="mt-2 space-y-1 text-[11px] text-warning">
                {m.blockers.map((b) => (
                  <li key={b}>· {b}</li>
                ))}
              </ul>
            )}

            <ul className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-2">
              {m.factors.map((f) => (
                <li key={f.key}>
                  <span
                    className={
                      f.status === "match"
                        ? "text-success"
                        : f.status === "miss"
                          ? "text-warning"
                          : "text-muted-foreground"
                    }
                  >
                    {f.status === "match" ? "✓" : f.status === "miss" ? "✕" : "?"}
                  </span>{" "}
                  <strong className="text-white/80">{f.label}:</strong> {f.detail}
                </li>
              ))}
            </ul>
          </article>
        ))
      )}
    </div>
  );
}

function DispositionPanel({ buyerCount }: { buyerCount: number }) {
  const runMatch = useServerFn(matchBuyersForDisposition);
  const [form, setForm] = useState({
    label: "",
    purchasePrice: "",
    arv: "",
    repairs: "",
    assignmentFee: "10000",
    postalCode: "",
    propertyType: "single_family",
  });

  const set =
    (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const match = useMutation({
    mutationFn: () =>
      runMatch({
        data: {
          label: form.label,
          purchasePrice: Number(form.purchasePrice),
          arv: Number(form.arv),
          repairs: Number(form.repairs),
          assignmentFee: Number(form.assignmentFee),
          postalCode: form.postalCode,
          propertyType: form.propertyType,
        },
      }),
    onError: (e: Error) => toast.error(e.message),
  });

  const buyerPrice = (Number(form.purchasePrice) || 0) + (Number(form.assignmentFee) || 0);

  return (
    <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
      <h2 className="text-sm font-semibold">Disposition — deal under contract</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Type the contract in and see which of your buyers it actually fits, with the reason for each
        factor. Nothing is sent to anyone.
      </p>

      <form
        className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          match.mutate();
        }}
      >
        <Field id="d-label" label="Label">
          <input
            id="d-label"
            value={form.label}
            onChange={set("label")}
            placeholder="Elm St contract"
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-price" label="Purchase price">
          <input
            id="d-price"
            type="number"
            value={form.purchasePrice}
            onChange={set("purchasePrice")}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-arv" label="ARV">
          <input
            id="d-arv"
            type="number"
            value={form.arv}
            onChange={set("arv")}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-repairs" label="Repairs">
          <input
            id="d-repairs"
            type="number"
            value={form.repairs}
            onChange={set("repairs")}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-fee" label="Desired assignment">
          <input
            id="d-fee"
            type="number"
            value={form.assignmentFee}
            onChange={set("assignmentFee")}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-zip" label="ZIP code">
          <input
            id="d-zip"
            inputMode="numeric"
            value={form.postalCode}
            onChange={set("postalCode")}
            placeholder="62704"
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </Field>
        <Field id="d-type" label="Property type">
          <select
            id="d-type"
            value={form.propertyType}
            onChange={set("propertyType")}
            className="focus-key w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          >
            <option value="single_family">Single family</option>
            <option value="multi_family">Multi family</option>
            <option value="condo">Condo</option>
            <option value="townhouse">Townhouse</option>
            <option value="manufactured">Manufactured</option>
            <option value="land">Land</option>
          </select>
        </Field>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={match.isPending}
            className="focus-key w-full rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
          >
            {match.isPending ? "Scoring…" : "Find buyers"}
          </button>
        </div>
      </form>

      <p className="mt-2 text-[11px] text-muted-foreground">
        Buyer pays{" "}
        <span className="text-gold">
          {buyerPrice.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          })}
        </span>{" "}
        — your contract price plus your assignment.
      </p>

      {buyerCount === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Add a buy box below first — matching needs saved buyers to compare against.
        </p>
      )}

      {match.data && <MatchResults data={match.data} />}
    </section>
  );
}
